/**
 * PWA: instalación, service worker y actualización forzada.
 *
 * El problema concreto que resuelve la actualización forzada: en el celular no
 * hay Ctrl+Shift+R. Si el navegador se quedó con un `index.html` viejo —que es
 * lo único que conserva su URL entre despliegues— la app sigue cargando los
 * bundles anteriores y el usuario no tiene forma de salir de ahí. Este módulo
 * le da esa forma, en un botón.
 */

import { APP_BUILD } from '../appVersion';

/** Dónde vive el archivo que declara la versión publicada. */
const URL_VERSION = `${process.env.PUBLIC_URL || ''}/version.json`;

/** Dónde vive el service worker. */
const URL_SW = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

// ── Versión ───────────────────────────────────────────────────────────────────

/** La versión que está corriendo ahora mismo en el navegador. */
export const versionActual = () => Number(APP_BUILD?.version) || 0;

/**
 * La versión publicada en el servidor.
 *
 * Se pide con `no-store` para saltear la caché HTTP, y el service worker
 * tampoco intercepta este archivo: si se sirviera desde caché, la app nunca se
 * enteraría de que hay algo nuevo, que es exactamente lo contrario de lo que
 * buscamos.
 *
 * Devuelve null ante cualquier problema: sin conexión no hay que molestar.
 */
export async function versionPublicada() {
  try {
    const res = await fetch(`${URL_VERSION}?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const datos = await res.json();
    const version = Number(datos?.version);
    return version > 0 ? { version, builtAt: datos.builtAt || '' } : null;
  } catch {
    return null;
  }
}

/** ¿Hay una versión más nueva publicada que la que está corriendo? */
export async function hayVersionNueva() {
  const actual = versionActual();
  if (!actual) return null;
  const publicada = await versionPublicada();
  if (!publicada) return null;
  return publicada.version > actual ? publicada : null;
}

// ── Actualización forzada ─────────────────────────────────────────────────────

/**
 * Deja la app como si se hubiera instalado por primera vez.
 *
 * En orden, y el orden importa:
 *   1. Borra las cachés del service worker y lo desregistra. Si sólo se
 *      recargara, el SW podría volver a servir lo viejo.
 *   2. Vuelve a pedir con `cache: 'reload'` cada script y hoja de estilo que la
 *      página tiene cargados, más el propio documento. Esto pisa la caché HTTP
 *      del navegador, que es la que retiene el `index.html` con las URLs de los
 *      bundles anteriores.
 *   3. Navega con un parámetro nuevo, para que el documento se pida entero y no
 *      salga del historial ni de la bfcache.
 *
 * No toca `localStorage` ni `sessionStorage`: la sesión, el país elegido y el
 * banner ya visto tienen que sobrevivir. Actualizar no es cerrar sesión.
 */
export async function forzarActualizacionCompleta() {
  try {
    if (window.caches?.keys) {
      const claves = await caches.keys();
      await Promise.all(claves.map(k => caches.delete(k)));
    }
    if (navigator.serviceWorker?.getRegistrations) {
      const registros = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registros.map(r => r.unregister()));
    }
  } catch (err) {
    console.error('No se pudieron limpiar el service worker y las cachés:', err);
  }

  try {
    const recursos = [...document.querySelectorAll('script[src], link[rel="stylesheet"][href]')]
      .map(el => el.src || el.href)
      .filter(Boolean);
    recursos.push(window.location.href.split('#')[0]);
    await Promise.allSettled(
      recursos.map(url => fetch(url, { cache: 'reload', credentials: 'same-origin' }))
    );
  } catch (err) {
    console.error('No se pudieron volver a descargar los archivos de la app:', err);
  }

  // Se conservan el país y la vista: actualizar no debería devolver al usuario
  // a la portada perdiendo dónde estaba.
  const url = new URL(window.location.href);
  url.searchParams.set('_fresh', String(Date.now()));
  window.location.replace(url.toString());
}

// ── Service worker ────────────────────────────────────────────────────────────

/**
 * Registra el service worker.
 *
 * Sólo en producción: con el servidor de desarrollo, un SW que cachea confunde
 * más de lo que ayuda —cuesta distinguir un cambio que no tomó de uno que no
 * funciona—. En `localhost` la recarga normal ya alcanza.
 */
export function registrarServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(URL_SW).catch(err => {
      // Que falle no rompe la app: simplemente no se puede instalar ni usar
      // sin conexión.
      console.warn('No se pudo registrar el service worker:', err);
    });
  });
}

// ── Instalación ───────────────────────────────────────────────────────────────

/**
 * Captura el evento que el navegador dispara cuando la app es instalable.
 *
 * Chrome sólo permite abrir el diálogo de instalación desde un gesto del
 * usuario y con el evento guardado de antes, así que hay que interceptarlo
 * apenas ocurre —antes de que React monte cualquier componente— y guardarlo.
 * Por eso vive acá y no dentro de un componente.
 */
let eventoInstalacion = null;
const suscriptores = new Set();

function avisar() {
  for (const fn of suscriptores) {
    try { fn(Boolean(eventoInstalacion)); } catch { /* un suscriptor roto no debe frenar al resto */ }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    eventoInstalacion = e;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    eventoInstalacion = null;
    avisar();
  });
}

/** ¿El navegador ofrece instalar la app ahora mismo? */
export const sePuedeInstalar = () => Boolean(eventoInstalacion);

/** Avisa cuando cambia la disponibilidad de instalación. Devuelve el desuscriptor. */
export function alCambiarInstalable(fn) {
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

/**
 * Abre el diálogo de instalación del navegador.
 * Devuelve true si el usuario aceptó.
 */
export async function instalarApp() {
  if (!eventoInstalacion) return false;
  const evento = eventoInstalacion;
  eventoInstalacion = null;
  avisar();
  try {
    evento.prompt();
    const { outcome } = await evento.userChoice;
    return outcome === 'accepted';
  } catch {
    return false;
  }
}

/** ¿La app se está usando ya instalada (no en una pestaña del navegador)? */
export function estaInstalada() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}
